<template>
    <v-row class="order-info-base" no-gutters>
        <v-col v-if="showImage" :cols="12" md="2" class="pr-md-4">
            <v-img
                tile
                class="order-info-base__img thumb"
                :src="$filters.imageUrl(serviceImage, 350)"
                :lazy-src="$filters.srcHost('/placeholder.png')"
                min-height="100"
                :eager="true"
                :max-height="$breakpoint.smAndDown ? 200 : undefined"
                @error="onImgError"
            >
                <template v-slot:placeholder>
                    <v-row v-show="!imgError && serviceImage" class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey-lighten-5" />
                    </v-row>
                </template>
            </v-img>
            <slot name="belowImg" />
        </v-col>
        <v-col
            v-if="serviceCheckIn || serviceCheckOut"
            class="order-info-base__icons px-md-2 pt-4 pt-md-0"
            :cols="12"
            :md="showImage ? 5 : 7"
        >
            <div>
                <slot name="additionalInfo" />
            </div>
            <div v-if="serviceCheckIn" class="order-info-base-info order-info-base__icons-checkin">
                <span class="order-info-base-info__icon">
                    <v-icon start class="date-icon" size="x-small">mdi-clock-in</v-icon>
                    <span class="text-body-2">{{ serviceCheckInLabel }}</span>
                </span>
                <span class="order-info-base-info__text" v-html="serviceCheckIn" />
            </div>
            <div v-if="serviceCheckOut" class="order-info-base-info order-info-base__icons-checkout">
                <span class="order-info-base-info__icon">
                    <v-icon start class="date-icon" size="x-small">mdi-clock-out</v-icon>
                    <span class="text-body-2">{{ serviceCheckOutLabel }}</span>
                </span>
                <span class="order-info-base-info__text" v-html="serviceCheckOut" />
            </div>
            <slot name="extraTime" />
            <slot />
            <div v-if="service.refNum" class="order-info-base-info">
                <span class="order-info-base-info__icon">
                    <v-icon start size="x-small" class="date-icon"> mdi-pound </v-icon>
                    <span class="text-body-2">{{ $t('reference_number') }}</span>
                </span>
                <span class="order-info-base-info__text" v-html="service.refNum" />
            </div>
            <div v-if="service.hotelRefNum" class="order-info-base-info">
                <span class="order-info-base-info__icon">
                    <v-icon start size="x-small" class="date-icon"> mdi-pound </v-icon>
                    <span class="text-body-2">{{ $t('hotel_reference_number') }}</span>
                </span>
                <span class="order-info-base-info__text" v-html="service.hotelRefNum" />
            </div>
        </v-col>
        <template v-if="!notItineraryStart">
            <slot v-if="personRole !== 'dispatcher'" name="price">
                <v-col
                    v-if="(!isPackage && !isReadOnly) || isTO1 || (isTripAssistantKeyExist && isReadOnly)"
                    cols="12"
                    order="10"
                    order-md="0"
                    md="3"
                    class="ml-auto px-md-3 pb-4 pb-md-0 border-e-thin border-s-thin"
                >
                    <order-prices
                        v-if="showAllServicesPrice"
                        :service-card-guarantee="isPayAtHotel"
                        :supplier-cancel-penalties="supplierPrice.cancelPenalty"
                        :supplier-payment-status="service.supplierPaymentStatus"
                        :loyalty-points="!isGuest ? service.loyaltyPoints : null"
                        :supplier-price="supplierPrice.price"
                        :client-cancel-penalties="clientPrice.cancelPenalty"
                        :customer-payment-status="service.customerPaymentStatus"
                        :client-price="isStaticPackage ? clientCustomComplexTourPrice : clientPrice.price"
                        :is-static-package="isStaticPackage"
                        :alignment="isSupplier && !isConfirmationPendingStatus ? 'justify-end' : 'justify-space-around'"
                        :service="service"
                    >
                        <template v-if="!isSupplier" v-slot:violation>
                            <div v-if="service.violation">
                                <corporate-policy-violation :policies="service.corporatePolicies" />
                            </div>
                        </template>
                    </order-prices>
                    <div v-else-if="isShowServiceDiscount" class="full-width text-info">
                        <div class="commission-block align-center">
                            <span class="text-body-2">{{ $t('discount') }}</span>
                            <span v-if="isTO1" class="text-end text-subtitle-1 font-weight-medium">
                                {{ $filters.priceFormat(clientDiscount.amount, clientPrice.currency) }}
                                <br />
                            </span>
                        </div>
                        <span>
                            {{ clientDiscount.name }}
                        </span>
                    </div>
                </v-col>
            </slot>
            <v-col order="11" order-md="0" md="2" class="ml-auto flex-column text-center order-service-buttons pl-md-4">
                <v-btn
                    v-if="canRetrieveHotelStatus"
                    :block="$breakpoint.smAndDown"
                    :size="$breakpoint.smAndDown ? 'small' : 'default'"
                    class="mb-3 order-info-btn"
                    variant="flat"
                    :loading="checkStatusActive"
                    :disabled="checkStatusActive"
                    @click="checkStatus"
                >
                    {{ $t('check_status') }}
                </v-btn>
                <template v-if="isShowCancellationPendingConfirm">
                    <v-btn
                        variant="flat"
                        color="primary"
                        :block="$breakpoint.smAndDown"
                        :size="$breakpoint.smAndDown ? 'small' : 'default'"
                        class="mb-3 text-white order-info-btn"
                        :class="locale === 'ru' ? 'supplier-action-btn' : ''"
                        @click="showReservationsModal('CANCEL_CONFIRM')"
                    >
                        {{ $t('supplier.confirm') }}
                    </v-btn>
                </template>

                <template
                    v-if="
                        (isSupplier || isTO1) &&
                        (isConfirmationPendingStatus || isModificationPendingStatus) &&
                        personRole !== 'dispatcher' &&
                        (isShowConfirmBtn || isShowRejectBtn || isShowSendEmailBtn)
                    "
                >
                    <v-btn
                        v-if="isShowConfirmBtn"
                        variant="flat"
                        color="primary"
                        :block="$breakpoint.smAndDown"
                        :size="$breakpoint.smAndDown ? 'small' : 'default'"
                        class="mb-3 text-white order-info-btn"
                        :class="locale === 'ru' ? 'supplier-action-btn' : ''"
                        @click="showReservationsModal('CONFIRM')"
                    >
                        {{ $t('supplier.confirm') }}
                    </v-btn>
                    <v-btn
                        v-if="isShowRejectBtn"
                        variant="flat"
                        color="red"
                        :block="$breakpoint.smAndDown"
                        :size="$breakpoint.smAndDown ? 'small' : 'default'"
                        class="mb-3 text-white order-info-btn"
                        :class="locale === 'ru' ? 'supplier-action-btn' : ''"
                        @click="showReservationsModal('REJECT')"
                    >
                        {{ $t('supplier.reject') }}
                    </v-btn>
                </template>
                <v-btn
                    v-if="
                        isShowSendEmailBtn &&
                        (isSupplier || isTO1) &&
                        isConfirmationPendingStatus &&
                        personRole !== 'dispatcher' &&
                        (isShowConfirmBtn || isShowRejectBtn || isShowSendEmailBtn)
                    "
                    :block="$breakpoint.smAndDown"
                    :size="$breakpoint.smAndDown ? 'small' : 'default'"
                    :class="locale === 'ru' ? 'supplier-action-btn' : ''"
                    variant="text"
                    class="mb-3 order-info-btn order-info-btn--send-email"
                    @click="sendEmail"
                >
                    {{ $t('send_email') }}
                </v-btn>
                <template
                    v-if="(!isPackage || isTO1) && isShowSupplierButtons && !isGuest && personRole !== 'dispatcher'"
                >
                    <template v-if="service.status === 'Quote' && showPrice && service.serviceType !== 'EXTRASERVICE'">
                        <v-btn
                            variant="flat"
                            color="primary"
                            :block="$breakpoint.smAndDown"
                            :size="$breakpoint.smAndDown ? 'small' : 'default'"
                            class="mb-3 text-white order-info-btn"
                            @click="book"
                        >
                            {{ $t('book') }}
                        </v-btn>
                        <a
                            class="check-availability modify-link cursor-pointer mb-3"
                            :class="checkAvailabilityActive ? 'text-secondary' : 'text-black'"
                            @click="checkAvailability"
                        >
                            {{ $t('modify.check_availability') }}
                        </a>
                    </template>
                    <v-btn
                        v-if="
                            showPaymentAndInvoiceStatus &&
                            canPayService &&
                            !isPayAtHotel &&
                            !$conf.public.account.paymentScheduler
                        "
                        variant="flat"
                        color="primary"
                        :block="$breakpoint.smAndDown"
                        :size="$breakpoint.smAndDown ? 'small' : 'default'"
                        class="mb-3 text-white order-info-btn"
                        @click="payService"
                    >
                        {{ $t('pay_service') }}
                    </v-btn>
                    <voucher-dialog
                        v-model="isShowVoucherDialog"
                        :is-loading="voucherLoading"
                        :title="isFlight ? $t('flights.e_ticket') : $t('voucher')"
                        @download-voucher="downloadVoucher(service)"
                        @send-on-email="sendVoucherOnEmail(service, $event)"
                    />
                    <v-btn
                        v-if="isShowVoucherBtn && voucherAvailable"
                        variant="flat"
                        color="primary"
                        data-cy="show-voucher"
                        :block="$breakpoint.smAndDown"
                        :size="$breakpoint.smAndDown ? 'small' : 'default'"
                        class="mb-3 text-white order-info-btn"
                        :loading="voucherLoading"
                        :disabled="voucherLoading"
                        @click="isShowVoucherDialog = true"
                    >
                        {{ $t('voucher') }}
                    </v-btn>
                    <v-checkbox
                        v-if="showVoucherPrintControl"
                        :model-value="service.voucherPrintControl"
                        true-value="ALLOW"
                        false-value="NOT_ALLOW"
                        @update:model-value="updateVoucherPrintControl"
                    >
                        <template v-slot:label>
                            <span class="text-body-2">
                                {{ $t('allow_client_to_download_voucher') }}
                            </span>
                        </template>
                    </v-checkbox>
                    <template v-if="isFlight && !statusForbidden && $conf.public.account.issueFlightTicket">
                        <v-btn
                            v-if="voucherAvailable"
                            variant="flat"
                            color="grey"
                            :block="$breakpoint.smAndDown"
                            :size="$breakpoint.smAndDown ? 'small' : 'default'"
                            class="mb-3 text-white order-info-btn"
                            :loading="voucherLoading"
                            :disabled="voucherLoading"
                            @click="isShowVoucherDialog = true"
                        >
                            {{ $t('flights.e_ticket') }}
                        </v-btn>
                        <v-btn
                            v-else
                            variant="flat"
                            color="grey"
                            :block="$breakpoint.smAndDown"
                            :size="$breakpoint.smAndDown ? 'small' : 'default'"
                            class="mb-3 text-white order-info-btn"
                            @click="$emit('issue-ticket', service)"
                        >
                            {{ $t('flights.issue_ticket') }}
                        </v-btn>
                    </template>
                    <v-btn
                        v-if="isFlight && canRetrieve && !isCharter && showCheckStatusBtn"
                        :block="$breakpoint.smAndDown"
                        :size="$breakpoint.smAndDown ? 'small' : 'default'"
                        class="mb-3 order-info-btn"
                        variant="flat"
                        :loading="checkStatusActive"
                        :disabled="checkStatusActive"
                        @click="checkStatus"
                    >
                        {{ $t('check_status') }}
                    </v-btn>
                    <v-btn
                        v-if="ticketVoidingAllowed"
                        :block="$breakpoint.smAndDown"
                        :size="$breakpoint.smAndDown ? 'small' : 'default'"
                        variant="flat"
                        class="mb-3 order-info-btn"
                        @click="showTicketVoidingModal"
                    >
                        {{ $t('voiding') }}
                    </v-btn>
                    <a
                        v-if="
                            isTO1 && $conf.public.account.modifyServiceManual && service.serviceType !== 'EXTRASERVICE'
                        "
                        class="mb-3 cursor-pointer text-black modify-link"
                        @click="modifyServiceManual"
                    >
                        {{ $t('manual_correction') }}
                    </a>
                    <add-offline-service-dialog
                        v-if="
                            isTO1 && $conf.public.account.modifyServiceManual && service.serviceType === 'EXTRASERVICE'
                        "
                        :order-travelers="orderTravelers"
                        :order-process-id="order.processId"
                        :order="order"
                        :service-to-modify="service"
                        :client-person="order.clientPerson"
                        :client-type="order.clientType"
                        :customer-company-id="order.customerCompanyId"
                        :info="info"
                        @send-request="refreshOrder"
                        @update-linked-hotel="$emit('update-linked-hotel', $event)"
                    />
                    <a v-if="isShowEditLink" class="mb-2 cursor-pointer text-black modify-link" @click="modifyService">
                        {{ $t('edit') }}
                    </a>
                    <a
                        v-if="isShowCancelButton && !isPackage && !isBankFee"
                        class="mb-2 cursor-pointer text-red cancel-link"
                        @click="cancel"
                    >
                        {{ $t('cancel') }}
                    </a>
                    <template v-if="isShowSendEmailBtn && !isConfirmationPendingStatus">
                        <v-divider class="my-1" />
                        <v-btn
                            :block="$breakpoint.smAndDown"
                            :size="$breakpoint.smAndDown ? 'small' : 'default'"
                            :class="locale === 'ru' ? 'supplier-action-btn' : ''"
                            variant="text"
                            class="mb-3 order-info-btn order-info-btn--send-email"
                            @click="sendEmail"
                        >
                            {{ $t('send_email') }}
                        </v-btn>
                    </template>
                    <v-btn
                        v-if="isShowDueToConfirmBtn"
                        block
                        :size="$breakpoint.smAndDown ? 'small' : 'default'"
                        class="text-caption"
                        variant="text"
                        @click="showDueToConfirmDateModal"
                    >
                        {{ $t('due_to_confirm_by') }}<br />{{ $filters.dateTimeFormat(service.dueToConfirmDate) }}
                    </v-btn>
                    <div
                        v-if="isTO1 && isOwnProduct && isReservationReportMailEnabled"
                        v-ripple
                        class="rounded pa-1 cursor-pointer"
                        style="text-transform: none"
                        @click="isShowSentToSupplierModal = true"
                    >
                        <span
                            v-if="service.serviceSentToSupplier"
                            class="text-success ms-2 text-subtitle-2 font-weight-regular"
                        >
                            {{ $t('sent_to_supplier') }}
                            <template v-if="service.serviceSentToSupplierDateTime">
                                (
                                <span>
                                    {{ $filters.dateFormat(service.serviceSentToSupplierDateTime) }}
                                </span>
                                <span>
                                    {{ $filters.hoursFromDate(service.serviceSentToSupplierDateTime) }}
                                </span>
                                )
                            </template>
                        </span>
                        <span v-else class="text-error ms-2 text-subtitle-2 font-weight-regular">
                            {{ $t('not_sent_to_supplier') }}
                        </span>
                    </div>
                </template>
            </v-col>
            <slot name="additionalInfo2" />
            <v-col cols="12" md="12" class="order-info-base__travelers">
                <slot name="subtitle" />
                <slot name="travelers" :empty-additional-fields="touristEmptyAdditionalFields">
                    <v-divider
                        v-if="tariffName || serviceLabel || service.travelers?.length"
                        class="d-block d-md-none my-3"
                    />
                    <div
                        v-if="serviceLabel || tariffName"
                        class="order-info-base__travelers-title d-flex align-center text-subtitle-1 mb-3"
                    >
                        <template v-if="serviceLabel">
                            <ProductVariantLabel v-if="service.productVariantId" :product="service" class="me-1" />
                            <v-icon v-else start size="x-small"> {{ getServiceTypeIcon(service) }}</v-icon>
                            <span class="order-info-base__travelers-title-txt text-subtitle-2 font-weight-bold me-1">
                                {{ serviceLabel }}
                            </span>
                        </template>
                        <span v-if="tariffName" class="text-grey-lighten-1 text-subtitle-2 font-weight-regular">
                            ({{ tariffName }})
                        </span>
                    </div>
                    <v-divider v-if="serviceLabel || service.travelers?.length" class="d-none d-md-block my-3" />
                    <travelers
                        :travelers="service.travelers"
                        :product="service.serviceType"
                        :details="service.serviceDetails"
                        :service="service"
                        :order-end-date="orderEndDate"
                        :empty-additional-fields="touristEmptyAdditionalFields"
                    >
                        <template
                            v-if="
                                service.serviceType === 'CRUISE' ||
                                service.serviceDetails[0].extraServiceProductType === 'ACCOMMODATION'
                            "
                            v-slot:mealType="{traveler}"
                        >
                            <v-col cols="12" md="2" class="pb-0">
                                <small class="d-block traveler-label text-body-2">
                                    {{ $t('filterTitles.mealTypes') }}
                                </small>
                                <span
                                    v-if="service.serviceType === 'CRUISE'"
                                    class="text-uppercase text-black font-weight-bold"
                                >
                                    {{ traveler.mealType || '' }}
                                </span>
                                <span
                                    v-if="service.serviceDetails[0].extraServiceProductType === 'ACCOMMODATION'"
                                    class="text-uppercase text-black font-weight-bold"
                                >
                                    {{ service.serviceDetails[0].mealType || '' }}
                                </span>
                            </v-col>
                        </template>
                    </travelers>
                </slot>
                <referrer v-if="referrer" :referrer="referrer" />
            </v-col>
            <v-col v-if="service.extraServices.length" cols="12" md="12" class="mt-2 my-md-2">
                <div class="text-body-2 text-medium-emphasis">
                    {{ $t('extra_services') }}
                </div>
                <div v-for="extraService in service.extraServices" :key="extraService.id" class="text-caption">
                    <span>
                        {{ extraService.name }}
                        <span v-if="extraService.quantity"> - {{ getServiceDescription(extraService) }} </span>
                        <span v-if="extraService.prices && showPrice">
                            ({{
                                $filters.priceFormat(
                                    extraServiceClientPrice(extraService.prices)?.amount,
                                    extraServiceClientPrice(extraService.prices)?.currency
                                )
                            }})
                        </span>
                        <span
                            v-if="'EARLY_CHECKIN' === extraService.type && service.serviceDetails[0].earlyCheckIn"
                            :class="statusColor(service.serviceDetails[0].earlyCheckIn.status)"
                        >
                            {{ $t(`bookingStatus.${status(service.serviceDetails[0].earlyCheckIn.status)}`) }}
                        </span>
                        <span
                            v-if="'LATE_CHECKOUT' === extraService.type && service.serviceDetails[0].lateCheckOut"
                            :class="statusColor(service.serviceDetails[0].lateCheckOut.status)"
                        >
                            {{ $t(`bookingStatus.${status(service.serviceDetails[0].lateCheckOut.status)}`) }}
                        </span>
                    </span>
                </div>
            </v-col>
            <v-col
                v-if="service.bookingAdditionalQuestions && service.bookingAdditionalQuestions.length"
                cols="12"
                md="12"
                class="py-2"
            >
                <div class="text-subtitle-2 text-sm-subtitle-1">
                    {{ `${$t('answers_to_questions')}: ` }}
                </div>
                <div class="d-flex flex-column">
                    <span v-for="(answerObj, i) in service.bookingAdditionalQuestions" :key="answerObj.id">
                        {{ `${i + 1}. ${answerObj.question}: ` }}
                        <span v-if="answerObj.answer">
                            {{ answerObj.answer }}
                        </span>
                        <span v-else class="text-error font-weight-medium">
                            {{ $t('please_answer_when_booking') }}
                        </span>
                    </span>
                </div>
            </v-col>
            <v-col v-if="isTO1 || service.comments" order="6" order-md="0" cols="12" md="12">
                <v-divider class="my-2" />
                <div class="text-subtitle-2 font-weight-bold">
                    {{ $t('comments') }}
                </div>
                <div>
                    <span v-if="service.comments" class="mr-2" v-html="service.comments" />
                    <span v-else class="text-medium-emphasis">{{ $t('you_can_add_a_comment') }}</span>
                    <v-btn
                        v-if="isTO1 && !authStore.isAccountant"
                        icon="mdi-pencil"
                        variant="text"
                        size="x-small"
                        class="text-medium-emphasis"
                        @click="showCommentsEditingModal"
                    />
                </div>
                <v-divider class="d-block d-md-none my-2" />
            </v-col>
            <sent-to-supplier-modal
                v-model="isShowSentToSupplierModal"
                :process-id="service.processId"
                :order-id="order.orderId"
                :status-initial="service.serviceSentToSupplier"
            />
        </template>
    </v-row>
</template>

<script lang="ts">
    import {Component, Inject, mixins, Prop} from 'vue-facing-decorator'
    import OrderPrices from '~src/components/account/snippets/orderPrices.src'
    import {
        EventBus,
        ORDER_SERVICE_BOOK,
        PAY_SERVICE_EVENT,
        SHOW_DUE_TO_CONFIRM_DATE_SERVICE_MODAL,
        SHOW_MODIFY_MANUAL_MODAL,
        SHOW_MODIFY_MODAL_EVENT,
        SHOW_NOTIFICATION_MODAL,
        SHOW_RESERVATION_STATUS_MODAL,
        SHOW_SEND_EMAIL_MODAL,
        SHOW_SERVICE_COMMENTS_EDITING_MODAL,
        SHOW_TICKET_VOIDING_MODAL,
    } from '@/utils/event-bus'
    import {FlightOrderInfoBaseMixin} from '@/components/account/mixins/FlightOrderInfoBaseMixin.vue'
    import {VoucherMixin} from '@/components/account/mixins/VoucherMixin.vue'
    import Travelers from '~src/components/account/orderServices/travelers.src'
    import CorporatePolicyViolation from '~/components/snippets/CorporatePolicyViolation.vue'
    import HotelStars from '~src/components/hotels/snippets/hotelStars.src'
    import Referrer from '@/components/account/orderServices/Referrer.vue'
    import {getServiceTypeIcon, UTCToDate} from '~src/utils/helpers'
    import {ServiceBase} from '@/components/account/orderServices/ServiceBase.vue'
    import {prepareBookResult, WARNING_MESSAGE_PRICE_CHANGED} from '@/utils/PrepareBookAdapter'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import AddOfflineServiceDialog from '~src/components/account/offlineServices/addOfflineServiceDialog.src'
    import SentToSupplierModal from '@/components/account/modals/SentToSupplierModal.vue'
    import getOrderTravelers from '~/adapters/orderTravelersAdapter'
    import ProductVariantLabel from '~/components/snippets/ProductVariantLabel.vue'

    //TODO Need to refactoring with related part of OrderServiceBookModal
    function availabilityNotificationHtml(text, color, discount, prepareBookResult) {
        let html =
            `<div class="text-body-1 d-flex flex-column text-${color}">
            ${text}` +
            (discount?.name
                ? `<span class="text-caption text-info">
                   ${discount.label}:
              <span>
                    ${discount.name}
              </span>
            </span>`
                : '') +
            '</div>'
        if (prepareBookResult?.availableForBooking) {
            const statusColor = prepareBookResult.onRequest ? 'warning' : 'success',
                statusLabel = prepareBookResult.onRequest
                    ? this.$t('filters_availability.on_request')
                    : this.$t('filters_availability.available')
            html +=
                '<div class="text-body-1 mt-2">' +
                `${this.$t('actual_availability')}: <span class="text-${statusColor}">${statusLabel}</span>` +
                '</div>'
        }
        return html
    }
    @Component({
        components: {
            ProductVariantLabel,
            SentToSupplierModal,
            AddOfflineServiceDialog,
            SupplierNameLabel,
            Referrer,
            HotelStars,
            OrderPrices,
            CorporatePolicyViolation,
            Travelers,
        },
    })
    export class OrderInfoBase extends mixins(FlightOrderInfoBaseMixin, VoucherMixin, ServiceBase) {
        @Prop() service
        @Prop() serviceLabel
        @Prop() tariffName
        @Prop({default: false}) isPackage
        @Prop() serviceCheckIn
        @Prop() serviceCheckOut
        @Prop({required: true}) orderEndDate
        @Prop({default: null}) serviceImage
        @Prop({default: false}) info
        @Prop({default: null}) packageFlightStatus
        @Prop({default: false, type: Boolean}) notItineraryStart
        @Inject({default: false}) isTripAssistantKeyExist
        @Inject({default: false}) isReadOnly
        @Inject({default: 0}) packageServicesLength

        imgError = false
        checkAvailabilityActive = false
        isShowSentToSupplierModal = false

        cancel() {
            this.$emit('cancel-service', this.service)
        }

        async refreshOrder() {
            await runtimeStore.refreshOrder()
        }

        getServiceTypeIcon(service) {
            return getServiceTypeIcon(service)
        }

        showReservationsModal(type) {
            EventBus.$emit(SHOW_RESERVATION_STATUS_MODAL, {
                type,
                processId: this.service.processId,
                orderId: this.order.orderId,
            })
        }

        showTicketVoidingModal() {
            EventBus.$emit(SHOW_TICKET_VOIDING_MODAL, {processId: this.service.processId})
        }

        getServiceDescription(extraService) {
            if (extraService.type === 'EARLY_CHECKIN') {
                return this.service.serviceDetailsContainer.accommodation.earlyCheckIn?.time
            }
            if (extraService.type === 'LATE_CHECKOUT') {
                return this.service.serviceDetailsContainer.accommodation.lateCheckOut?.time
            }
            return extraService.quantity
        }

        onImgError() {
            this.imgError = true
            return false
        }

        modifyService() {
            EventBus.$emit(SHOW_MODIFY_MODAL_EVENT, {
                service: this.service,
                orderId: this.order.orderId,
                info: this.info,
            })
        }

        book() {
            EventBus.$emit(ORDER_SERVICE_BOOK, {services: [this.service], order: this.order})
        }

        async checkAvailability() {
            if (this.checkAvailabilityActive) return
            this.checkAvailabilityActive = true
            try {
                const prepareBookResponse = await this.$api.prepareBookSaveOrder.post({
                    processIds: [this.service.processId.replace(/^.{6}/, '')],
                    checkAvailability: true,
                })
                const [result] = prepareBookResult(prepareBookResponse)
                if (result.availableForBooking) {
                    if (result.warningMessages.includes(WARNING_MESSAGE_PRICE_CHANGED)) {
                        const price = this.$filters.formatPrice(result.price),
                            supplierPrice = this.$filters.formatPrice(result.supplierPrice)
                        EventBus.$emit(
                            SHOW_NOTIFICATION_MODAL,
                            availabilityNotificationHtml.call(
                                this,
                                this.isTO1
                                    ? this.$t('warning_message.price_changed_to1-second', {price, supplierPrice})
                                    : this.$t('warning_message.service_current_price', {price}),
                                'warning',
                                {
                                    name: result.price.discountDetails,
                                    label: this.$t('discount'),
                                },
                                result
                            )
                        )
                    } else {
                        EventBus.$emit(
                            SHOW_NOTIFICATION_MODAL,
                            availabilityNotificationHtml.call(
                                this,
                                this.$t('success_message.offer_available'),
                                'success',
                                {
                                    name: result.price.discountDetails,
                                    label: this.$t('discount'),
                                },
                                result
                            )
                        )
                    }
                } else {
                    EventBus.$emit(
                        SHOW_NOTIFICATION_MODAL,
                        availabilityNotificationHtml.call(
                            this,
                            this.$t('error_message.offer_not_available'),
                            'error',
                            {
                                name: result.price.discountDetails,
                                label: this.$t('discount'),
                            },
                            result
                        )
                    )
                }
            } catch (e) {
                console.error(e)
                EventBus.$emit(
                    SHOW_NOTIFICATION_MODAL,
                    availabilityNotificationHtml.call(this, this.$t('error_message.offer_not_available'), 'error')
                )
            } finally {
                this.checkAvailabilityActive = false
            }
        }

        payService() {
            EventBus.$emit(PAY_SERVICE_EVENT, {orderId: this.orderId, objectId: this.objectId})
        }

        extraServiceClientPrice(prices) {
            return prices.find(({priceType}) => priceType === 'CLIENT')
        }

        statusColor(status) {
            return runtimeStore.orderServiceStatusColor(status)
        }

        status(status) {
            return runtimeStore.orderServiceStatus(status)
        }

        modifyServiceManual() {
            EventBus.$emit(SHOW_MODIFY_MANUAL_MODAL, {service: this.service, isStaticPackage: this.isStaticPackage})
        }

        get isStaticPackage() {
            return !!this.customComplexTourInfo && this.service.serviceContainerId
        }

        get customComplexTourInfo() {
            return this.order.services?.find(({serviceType}) => serviceType === 'CUSTOMCOMPLEXTOUR')
        }

        get clientCustomComplexTourPrice() {
            const {amount, currency} = this.customComplexTourInfo.salesTerms.find(({type}) => type === 'CLIENT').price
            return {amount: (amount / this.packageServicesLength).toFixed(2), currency}
        }

        sendEmail() {
            EventBus.$emit(SHOW_SEND_EMAIL_MODAL, {order: this.order, service: this.service})
        }

        showDueToConfirmDateModal() {
            if (!(this.service.serviceType === 'EXTRASERVICE' && this.service.status === 'PreConfirmed')) {
                EventBus.$emit(SHOW_DUE_TO_CONFIRM_DATE_SERVICE_MODAL, {
                    service: this.service,
                    orderId: this.order.orderId,
                })
            }
        }

        showCommentsEditingModal() {
            EventBus.$emit(SHOW_SERVICE_COMMENTS_EDITING_MODAL, {service: this.service, orderId: this.order.orderId})
        }

        get isShowDueToConfirmBtn() {
            if (['FLIGHT', 'OWNCHARTER'].includes(this.service.serviceType) && !this.isOwnProduct) {
                return false
            }

            return (
                this.service.dueToConfirmDate && !['Canceled', 'Confirmed'].includes(this.service.status) && this.isTO1
            )
        }

        get isOwnProduct() {
            return runtimeStore.ownProduct(this.service?.supplierCode)
        }

        get clientDiscount() {
            return this.service.salesTerms.find(({type}) => type === 'CLIENT').discount
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isTourOperator() {
            return authStore.isTourOperator
        }

        get personRole() {
            return authStore.personRole
        }

        get isShowEditLink() {
            return (
                (this.service.serviceType === 'ACCOMMODATION' || this.ownProduct) &&
                this.service.modificationAllowed &&
                (this.isTO1 || this.$conf.public.account.editService) &&
                this.service.serviceType !== 'EXTRASERVICE'
            )
        }

        get isShowCancelButton() {
            return (
                this.service.cancellationAllowed ||
                (this.packageFlightStatus && this.packageFlightStatus === 'PRECONFIRMED') ||
                (this.service.status === 'Confirmation pending' &&
                    this.$conf.public.account.cancelConfirmationPendingService) ||
                (this.service.serviceType === 'EXTRASERVICE' &&
                    this.service.status === 'Confirmed' &&
                    this.$dateFns.isAfter(this.$dateFns.parseISO(this.service.startDateTime), new Date()))
            )
        }

        get voucherAvailable() {
            return runtimeStore.isServiceVoucherAvailable(this.service)
        }

        get statusForbidden() {
            return runtimeStore.isServiceStatusForbidden(this.service)
        }

        get clientPrice() {
            return this.service.salesTerms.find(price => {
                return price.type === 'CLIENT'
            })
        }

        get supplierPrice() {
            return this.service.salesTerms.find(price => {
                return price.type === 'SUPPLIER'
            })
        }

        get isAgency() {
            return authStore.isAgency
        }

        get canPayService() {
            return (
                authStore.isAgency &&
                this.service.status === 'Confirmed' &&
                !['PAID', 'PARTLY_PAID'].includes(this.service.supplierPaymentStatus)
            )
        }

        get isSupplier() {
            return authStore.companyType === 'INTERNAL_SUPPLIER'
        }

        get isShowSupplierButtons() {
            return !this.isSupplier
        }

        get isGuest() {
            return authStore.isGuest
        }

        get isConfirmationPendingStatus() {
            return this.service.status === 'Confirmation pending'
        }

        get isModificationPendingStatus() {
            return this.service.status === 'Modification pending'
        }

        get isCancellationPendingStatus() {
            return this.service.status === 'Cancellation pending'
        }

        get isShowCancellationPendingConfirm() {
            return (
                this.isTO1 &&
                this.isCancellationPendingStatus &&
                this.personRole !== 'dispatcher' &&
                this.isOwnProduct &&
                this.service.serviceType !== 'EXTRASERVICE'
            )
        }

        get locale() {
            return this.$i18n.locale
        }

        get isPayAtHotel() {
            return this.service?.serviceDetailsContainer.accommodation
                ? this.service.serviceDetailsContainer.accommodation.cardGuarantee
                : false
        }

        get touristEmptyAdditionalFields() {
            return false
        }

        get showImage() {
            return true
        }

        get showPrice() {
            if (this.isReadOnly && !this.isTripAssistantKeyExist) return false
            return true
        }

        get showCheckStatusBtn() {
            return this.service.status !== 'Quote'
        }

        get referrer() {
            return this.service?.referrer
        }

        get ticketVoidingAllowed() {
            if (this.isFlight && this.service.status === 'Confirmed' && this.service.serviceDetails[0].lastVoidDate) {
                const date = UTCToDate(this.$dateFns.parseISO(this.service.serviceDetails[0].lastVoidDate))
                return this.$dateFns.isBefore(new Date(), date)
            }

            return false
        }

        get showPaymentAndInvoiceStatus() {
            return authStore.showPaymentAndInvoiceStatus
        }

        get showAllServicesPrice() {
            return this.isTO1 || this.order.clientPriceType === 'FOR_ALL_SERVICES' || !this.order.clientPriceType
        }

        get isShowServiceDiscount() {
            return (
                this.clientDiscount &&
                this.clientDiscount.name &&
                !(!this.isTourOperator && this.order.clientPriceType === 'ONLY_TOTAL')
            )
        }

        get isShowSendEmailBtn() {
            if (this.isBankFee || this.service.serviceType === 'EXTRASERVICE') {
                return false
            }

            return this.isTO1 || authStore.isTO2 || authStore.isAgency
        }

        get isShowVoucherBtn() {
            return !this.isFlight
        }

        get isShowConfirmBtn() {
            return this.service.serviceType !== 'EXTRASERVICE' && this.ownProduct
        }

        get isShowRejectBtn() {
            return this.service.serviceType !== 'EXTRASERVICE' && this.ownProduct
        }

        get ownProduct() {
            return runtimeStore.ownProduct(this.service.supplierCode)
        }

        get isReservationReportMailEnabled() {
            return this.$conf.public.reservationReport
        }

        get isBankFee() {
            return this.service.serviceDetails?.[0]?.extraServiceProductType === 'BANK_FEE'
        }

        get orderTravelers() {
            return getOrderTravelers(this.order)
        }

        get showVoucherPrintControl() {
            return (
                this.isTO1 &&
                ['supervisor', 'director', 'support'].includes(this.personRole) &&
                this.isShowVoucherBtn &&
                this.voucherAvailable &&
                ['NOT_ALLOW', 'ALLOW'].includes(this.service.voucherPrintControl)
            )
        }

        get serviceCheckInLabel() {
            const serviceType =
                this.service.serviceType === 'EXTRASERVICE'
                    ? this.service.serviceDetails?.[0]?.extraServiceProductType
                    : this.service.serviceType

            switch (serviceType) {
                case 'TRANSFER':
                case 'CARRENT':
                    return this.$t('transfers.pick_up')
                case 'ACCOMMODATION':
                    return this.$t('check_in')
                case 'OWNVISA':
                case 'INSURANCE':
                case 'TOUR':
                    return this.$t('start_date')
                default:
                    return this.$t('start_date_time')
            }
        }

        get serviceCheckOutLabel() {
            const serviceType =
                this.service.serviceType === 'EXTRASERVICE'
                    ? this.service.serviceDetails?.[0]?.extraServiceProductType
                    : this.service.serviceType

            switch (serviceType) {
                case 'TRANSFER':
                case 'CARRENT':
                    return this.$t('transfers.drop_off')
                case 'ACCOMMODATION':
                    return this.$t('check_out')
                case 'OWNVISA':
                case 'INSURANCE':
                case 'TOUR':
                    return this.$t('end_date')
                default:
                    return this.$t('end_date_time')
            }
        }

        updateVoucherPrintControl(voucherPrintControl) {
            const {serviceSentToSupplier, processId} = this.service
            try {
                this.$api.orders.put(this.order.orderId, {
                    servicesInfo: [
                        {
                            processId,
                            serviceSentToSupplier,
                            voucherPrintControl,
                        },
                    ],
                })
                this.service.voucherPrintControl = voucherPrintControl
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            }
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get canRetrieveHotelStatus() {
            return (
                this.service.serviceType === 'ACCOMMODATION' &&
                !this.ownProduct &&
                ['CONFIRMATION_PENDING', 'MODIFICATION_PENDING', 'CANCELLATION_PENDING'].includes(
                    runtimeStore.orderServiceStatus(this.service.status)
                )
            )
        }
    }

    export default OrderInfoBase
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';
    .date-icon {
        display: block;
        float: left;
    }

    .traveler-label,
    .title {
        line-height: 1;
    }

    .cancel-link,
    .modify-link {
        font-size: 14px;
        font-weight: normal;
        display: block;
        text-align: center;
    }

    .order-info-btn {
        width: 100%;
        font-size: 0.75rem;
    }

    .supplier-action-btn {
        font-size: 0.6rem;
    }

    .order-service-buttons .v-btn {
        white-space: normal;
    }

    @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
        .border-e-thin,
        .border-s-thin {
            border: none !important;
        }
    }
</style>
